import { NetworkStatus, useQuery } from "@apollo/client";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  CircularProgress,
  InputAdornment,
  List,
  ListItemButton,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Dialog } from "components/Dialog";
import { TextInput } from "components/TextInput";
import { gql } from "generated-graphql";
import { useDebounce } from "hooks/useDebounce";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState, type UIEvent } from "react";

const THREADS_PAGE_SIZE = 12;
const SEARCH_DEBOUNCE_MS = 300;
const LOAD_MORE_ROW_MIN_HEIGHT_PX = 72;
const LOAD_MORE_TRIGGER_BUFFER_PX = 8;
const SEARCH_SCROLLBAR_WIDTH_PX = 12;
const SEARCH_SCROLLBAR_OUTSET_PX = 24;
const LOAD_MORE_CONTENT_RIGHT_INSET_PX = SEARCH_SCROLLBAR_OUTSET_PX + 16;
const SEARCH_DIALOG_HEIGHT_PX = 640;
const SEARCH_DIALOG_MAX_WIDTH_PX = 920;
const SEARCH_DIALOG_MARGIN_PX = 40;
const PREVIEW_EXCERPT_MAX_CHARS = 88;
const UNTITLED_CHAT_LABEL = "Untitled chat";
const LOAD_MORE_LABEL = "Load more";

const GET_SCOUT_CONVERSATIONS = gql(`
  query GetScoutConversations($tenantId: ID!, $search: String, $page: Int, $pageSize: Int) {
    getScoutConversations(tenantId: $tenantId, search: $search, page: $page, pageSize: $pageSize) {
      count
      items {
        id
        title
        previewExcerpt
        updatedAt
        status
        allowMutations
      }
    }
  }
`);

type ScoutThreadListItem = {
  id: string;
  title?: string;
  previewExcerpt?: string;
  updatedAt: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSelectThread: (thread: ScoutThreadListItem) => void;
  dialogContainer?: () => Element | null;
};

function normalizePreviewExcerpt(previewExcerpt: string | null | undefined): string | undefined {
  const trimmed = previewExcerpt?.replace(/\s+/g, " ").trim();
  if (!trimmed) {
    return undefined;
  }

  if (trimmed.length <= PREVIEW_EXCERPT_MAX_CHARS) {
    return trimmed;
  }

  return `${trimmed.slice(0, PREVIEW_EXCERPT_MAX_CHARS - 1).trimEnd()}...`;
}

export function formatRelativeConversationTime(updatedAt: string): string {
  const now = DateTime.now();
  const timestamp = DateTime.fromISO(updatedAt);

  if (!timestamp.isValid) {
    return "";
  }

  const secondsAgo = now.diff(timestamp, "seconds").seconds;
  if (secondsAgo < 60) {
    return "now";
  }

  const minutesAgo = now.diff(timestamp, "minutes").minutes;
  if (minutesAgo < 10) {
    return "a few minutes ago";
  }

  if (minutesAgo < 60) {
    return (
      timestamp.toRelative({
        base: now,
        unit: "minutes",
        round: true,
      }) ?? "less than an hour ago"
    );
  }

  const hoursAgo = now.diff(timestamp, "hours").hours;
  if (hoursAgo < 24) {
    return (
      timestamp.toRelative({
        base: now,
        round: true,
      }) ?? "today"
    );
  }

  return timestamp.toRelativeCalendar({ base: now }) ?? timestamp.toFormat("LLL d");
}

export function SearchThreadsDialog({ open, onClose, onSelectThread, dialogContainer }: Props) {
  const searchInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const {
    searchInput,
    setSearchInput,
    heading,
    items,
    isInitialLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    emptyMessage,
    errorMessage,
  } = useScoutThreadsSearch(open);

  const focusSearchInput = useCallback(() => {
    const input = searchInputRef.current;
    if (!input) {
      return;
    }

    input.focus();
    const cursorPosition = input.value.length;
    input.setSelectionRange(cursorPosition, cursorPosition);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    const animationFrameId = window.requestAnimationFrame(focusSearchInput);
    return () => window.cancelAnimationFrame(animationFrameId);
  }, [focusSearchInput, open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      container={dialogContainer}
      noPrimaryAction
      contentSx={{ flex: 1, minHeight: 0, height: "100%" }}
      CollapseProps={{
        sx: {
          height: "100%",
          minHeight: 0,
          "&": {
            flex: 1,
            display: "flex",
            flexDirection: "column",
          },
          "& > .MuiCollapse-wrapper, & > .MuiCollapse-wrapper > .MuiCollapse-wrapperInner": {
            height: "100%",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          },
          "& > .MuiCollapse-wrapper > .MuiCollapse-wrapperInner > .MuiStack-root": {
            height: "100%",
            flex: 1,
            minHeight: 0,
          },
        },
      }}
      DialogContentProps={{
        sx: {
          minHeight: 0,
          paddingBottom: 2,
          overflow: "hidden",
        },
      }}
      PaperProps={{
        sx: {
          width: `min(${SEARCH_DIALOG_MAX_WIDTH_PX}px, calc(100% - ${SEARCH_DIALOG_MARGIN_PX}px))`,
          maxWidth: "none",
          height: `min(${SEARCH_DIALOG_HEIGHT_PX}px, calc(100% - ${SEARCH_DIALOG_MARGIN_PX}px))`,
          maxHeight: "none",
          display: "flex",
          flexDirection: "column",
        },
      }}
      slotProps={
        dialogContainer
          ? {
              root: {
                sx: {
                  position: "absolute",
                },
              },
              backdrop: {
                sx: {
                  position: "absolute",
                },
              },
            }
          : undefined
      }
      TransitionProps={{
        onEntered: focusSearchInput,
      }}
      title={
        <TextInput
          placeholder="Search threads..."
          fullWidth
          autoFocus
          inputRef={searchInputRef}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          }
        />
      }
    >
      <ThreadListSection
        heading={heading}
        items={items}
        loading={isInitialLoading}
        hasMore={hasMore}
        loadingMore={isLoadingMore}
        emptyMessage={emptyMessage}
        errorMessage={errorMessage}
        onLoadMore={loadMore}
        onSelectItem={onSelectThread}
      />
    </Dialog>
  );
}

function useScoutThreadsSearch(open: boolean) {
  const { tenantId } = useTenant();
  const [searchInput, setSearchInput] = useState("");
  const trimmedSearch = searchInput.trim();
  const [debouncedSearch] = useDebounce(trimmedSearch, SEARCH_DEBOUNCE_MS);
  const isSearchMode = trimmedSearch.length > 0;
  const search = debouncedSearch.length > 0 ? debouncedSearch : undefined;
  const pageSize = THREADS_PAGE_SIZE;
  const nextPageRef = useRef(0);
  // Increments whenever the active query context resets so an older fetchMore
  // completion cannot clear loading state or rewind pagination for a newer search.
  const fetchMoreRequestIdRef = useRef(0);
  const isFetchingMoreRef = useRef(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(
    function resetPaginationState() {
      nextPageRef.current = 0;
      fetchMoreRequestIdRef.current += 1;
      isFetchingMoreRef.current = false;
      setIsLoadingMore(false);
    },
    [open, pageSize, search]
  );

  const { data, error, loading, fetchMore, networkStatus } = useQuery(GET_SCOUT_CONVERSATIONS, {
    skip: !open || !tenantId,
    // Always refetch page 0 when the dialog opens or the debounced search changes
    // so recents/search results reflect the latest server ordering.
    fetchPolicy: "network-only",
    // Re-render on fetchMore state transitions so we can keep existing rows visible
    // while showing the bottom loading affordance for infinite scroll.
    notifyOnNetworkStatusChange: true,
    variables: tenantId
      ? {
          tenantId,
          search,
          page: 0,
          pageSize,
        }
      : undefined,
  });

  const items = useMemo(() => {
    return (data?.getScoutConversations.items ?? []).map((item) => ({
      id: item.id,
      title: item.title?.trim() ? item.title : undefined,
      previewExcerpt: normalizePreviewExcerpt(item.previewExcerpt),
      updatedAt: item.updatedAt,
    }));
  }, [data]);

  const totalCount = data?.getScoutConversations.count ?? 0;
  const hasMore = items.length < totalCount;
  const isInitialLoading =
    loading && items.length === 0 && networkStatus !== NetworkStatus.fetchMore;

  const loadMore = useCallback(async () => {
    if (!open || !tenantId || !hasMore || isFetchingMoreRef.current) {
      return;
    }

    const requestId = fetchMoreRequestIdRef.current;
    const nextPage = nextPageRef.current + 1;

    nextPageRef.current = nextPage;
    isFetchingMoreRef.current = true;
    setIsLoadingMore(true);

    try {
      await fetchMore({
        variables: {
          tenantId,
          search,
          page: nextPage,
          pageSize,
        },
      });
    } catch {
      if (fetchMoreRequestIdRef.current === requestId) {
        nextPageRef.current = nextPage - 1;
      }
    } finally {
      if (fetchMoreRequestIdRef.current === requestId) {
        isFetchingMoreRef.current = false;
        setIsLoadingMore(false);
      }
    }
  }, [fetchMore, hasMore, open, pageSize, search, tenantId]);

  return {
    searchInput,
    setSearchInput,
    heading: isSearchMode ? "Search results" : "Recents",
    items,
    isInitialLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    emptyMessage: isSearchMode ? "No threads found." : "No recent threads.",
    errorMessage: error ? "Unable to load threads." : undefined,
  };
}

function ThreadListSection({
  heading,
  items,
  loading,
  hasMore,
  loadingMore,
  emptyMessage,
  errorMessage,
  onLoadMore,
  onSelectItem,
}: {
  heading: string;
  items: ScoutThreadListItem[];
  loading?: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  emptyMessage: string;
  errorMessage?: string;
  onLoadMore: () => Promise<void> | void;
  onSelectItem: (item: ScoutThreadListItem) => void;
}) {
  const theme = useTheme();
  const scrollSurfaceColor = theme.palette.background.paper;
  const scrollThumbColor = alpha(
    theme.palette.text.primary,
    theme.palette.mode === "light" ? 0.24 : 0.34
  );
  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (loading || loadingMore || !hasMore) {
        return;
      }

      const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const isNearBottom = distanceFromBottom <= LOAD_MORE_TRIGGER_BUFFER_PX;

      if (isNearBottom) {
        void onLoadMore();
      }
    },
    [hasMore, loading, loadingMore, onLoadMore]
  );

  return (
    <Stack
      spacing={1.5}
      paddingBottom={theme.spacing(1)}
      sx={{ flex: 1, minHeight: 0, height: "100%" }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="caption">{heading}</Typography>
      </Stack>
      <Box
        sx={{
          flex: 1,
          height: 0,
          width: `calc(100% + ${SEARCH_SCROLLBAR_OUTSET_PX}px)`,
          maxWidth: "none",
          marginRight: `-${SEARCH_SCROLLBAR_OUTSET_PX}px`,
          minHeight: 0,
          backgroundColor: scrollSurfaceColor,
          overflow: "auto",
          overflowX: "hidden",
          scrollbarGutter: "stable",
          scrollbarWidth: "thin",
          scrollbarColor: `${scrollThumbColor} ${scrollSurfaceColor}`,
          "&::-webkit-scrollbar": {
            width: `${SEARCH_SCROLLBAR_WIDTH_PX}px`,
            backgroundColor: scrollSurfaceColor,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: scrollSurfaceColor,
            border: "none",
            boxShadow: "none",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: scrollThumbColor,
            border: "none",
            borderRadius: 999,
          },
          "&::-webkit-scrollbar-corner": {
            backgroundColor: scrollSurfaceColor,
          },
        }}
        data-testid="thread-list-scroll-container"
        onScroll={handleScroll}
      >
        {loading ? (
          <List disablePadding>
            {Array.from({ length: 5 }, (_, i) => (
              <ListItemButton key={i}>
                <Skeleton
                  data-testid="thread-list-skeleton"
                  variant="text"
                  width="100%"
                  height={40}
                />
              </ListItemButton>
            ))}
          </List>
        ) : errorMessage ? (
          <Alert severity="error">{errorMessage}</Alert>
        ) : items.length === 0 ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              px: 3,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {emptyMessage}
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {items.map((item) => {
              const relativeUpdatedAt = formatRelativeConversationTime(item.updatedAt);
              const hasPreviewExcerpt = Boolean(item.previewExcerpt);
              const titleLabel = item.title ?? UNTITLED_CHAT_LABEL;

              return (
                <ListItemButton
                  key={item.id}
                  onClick={() => onSelectItem(item)}
                  sx={{
                    width: "100%",
                    alignItems: "stretch",
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    px: 2,
                    py: 1.4,
                    "&:last-child": {
                      borderBottom: "none",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      minWidth: 0,
                      display: "flex",
                      alignItems: "baseline",
                      gap: 1.25,
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: 0,
                        flex: 1,
                        display: "flex",
                        alignItems: "baseline",
                        gap: 1.1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{
                          fontWeight: 500,
                          flexShrink: 0,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {titleLabel}
                      </Typography>

                      {hasPreviewExcerpt && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            minWidth: 0,
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            opacity: 0.72,
                          }}
                        >
                          {item.previewExcerpt}
                        </Typography>
                      )}
                    </Box>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        flexShrink: 0,
                        whiteSpace: "nowrap",
                        opacity: 0.58,
                      }}
                    >
                      {relativeUpdatedAt}
                    </Typography>
                  </Box>
                </ListItemButton>
              );
            })}
            {(hasMore || loadingMore) && (
              <ListItemButton
                disabled={loadingMore}
                onClick={() => {
                  if (!loadingMore) {
                    void onLoadMore();
                  }
                }}
                sx={{
                  minHeight: `${LOAD_MORE_ROW_MIN_HEIGHT_PX}px`,
                  justifyContent: "center",
                  borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                  pr: `${LOAD_MORE_CONTENT_RIGHT_INSET_PX}px`,
                }}
              >
                {loadingMore ? (
                  <Stack
                    width="100%"
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                    gap={1}
                  >
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">
                      Loading more
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {LOAD_MORE_LABEL}
                  </Typography>
                )}
              </ListItemButton>
            )}
          </List>
        )}
      </Box>
    </Stack>
  );
}
